import { useLookup } from 'hooks';
import { useSnackbar } from 'notistack';
import { ShortBook } from 'library/types';
import apiService from 'library/api-service';
import { GridRowId } from '@mui/x-data-grid-premium';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { renderAutoCompleteLocaleText, selectByIds, toIds } from 'utils';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';
import { Box, Button, TextField, DialogTitle, Autocomplete, CircularProgress, DialogContentText } from '@mui/material';

export type AssignProjectPostParams = {
  userId: number;
  bookIds: number[];
};

type AssignProjectProps = {
  open: boolean;
  userId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const AssignProject = ({ open, userId, handleClose: closeDialog, refresh }: AssignProjectProps) => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    items: books,
    loading: loadingBooks,
    loaded: booksLoaded,
  } = useLookup(apiService.getCompanies, { enabled: open });

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState<ShortBook[]>([]);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const postData: AssignProjectPostParams = {
      userId: userId as number,
      bookIds: toIds(selectedBooks),
    };

    try {
      setLoading(true);

      const response = await apiService.assignProject(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="users.assignProjectCompleted"
            defaultMessage="Proje atama işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificUser = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificUser(userId as number);

      if (response.isSuccess) {
        setSelectedBooks(selectByIds(books, response.data.bookIds));
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [books, userId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setSelectedBooks([]);
  };

  // useEffect hooks
  useEffect(() => {
    if (open && userId !== undefined && userId !== null && booksLoaded) {
      getSpecificUser();
    }
  }, [open, userId, booksLoaded, getSpecificUser]);

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="users.assignProject" defaultMessage="Proje Ata" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="users.assignProjectText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili kullanıcı için atama yapabilirsiniz."
            />
          </DialogContentText>
          <Autocomplete
            size="small"
            multiple
            options={books}
            loading={loadingBooks}
            value={selectedBooks}
            filterSelectedOptions
            disableCloseOnSelect
            {...renderAutoCompleteLocaleText(intl)}
            onChange={(_, newValue: ShortBook[]) => {
              setSelectedBooks(newValue);
            }}
            getOptionLabel={(option: ShortBook) => option.title}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="users.books" defaultMessage="Projeler" />}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBooks ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default AssignProject;
